/**
 * Componente PasskeyManager
 * Il Diario di Bordo - SaaS
 *
 * Gestione Passkeys/WebAuthn - registrazione, lista, rimozione.
 */

import { useState, useEffect } from 'react'
import { useTwoFactor } from '../../hooks/useTwoFactor'
import { 
  Fingerprint, 
  Plus, 
  Trash2, 
  Edit2, 
  Check, 
  X, 
  Loader2, 
  AlertCircle,
  Smartphone,
  Laptop,
  Key,
  Clock
} from 'lucide-react'

export default function PasskeyManager({ passkeys = [], onUpdate, maxPasskeys = 10 }) {
  const [isAdding, setIsAdding] = useState(false)
  const [deviceName, setDeviceName] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [deletingId, setDeletingId] = useState(null)
  const [backupCodes, setBackupCodes] = useState(null)
  
  const { 
    registerPasskey, 
    removePasskey, 
    renamePasskey, 
    loading, 
    error, 
    clearError,
    isWebAuthnSupported 
  } = useTwoFactor()

  // Reset stato quando cambia la lista
  useEffect(() => {
    setIsAdding(false)
    setDeviceName('')
    setEditingId(null)
    setDeletingId(null)
  }, [passkeys.length])

  const handleAddPasskey = async () => {
    if (!deviceName.trim()) return
    
    try {
      clearError()
      const data = await registerPasskey(deviceName.trim())
      
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes)
      }
      
      setIsAdding(false)
      setDeviceName('')
      onUpdate?.()
    } catch (err) {
      console.error('Errore registrazione passkey:', err)
    }
  }

  const handleRemovePasskey = async (credentialId) => {
    try {
      clearError()
      await removePasskey(credentialId)
      setDeletingId(null)
      onUpdate?.()
    } catch (err) {
      console.error('Errore rimozione passkey:', err)
    }
  }

  const handleRenamePasskey = async (credentialId) => {
    if (!editName.trim()) return
    
    try {
      clearError()
      await renamePasskey(credentialId, editName.trim())
      setEditingId(null)
      setEditName('')
      onUpdate?.()
    } catch (err) {
      console.error('Errore rinomina passkey:', err)
    }
  }

  const startEdit = (passkey) => {
    setEditingId(passkey.id)
    setEditName(passkey.friendlyName)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditName('')
  }

  const getDeviceIcon = (passkey) => {
    const type = passkey.deviceType?.toLowerCase()
    
    if (type === 'platform') {
      return <Smartphone className="h-5 w-5" />
    }
    if (type === 'cross-platform') {
      return <Key className="h-5 w-5" />
    }
    return <Fingerprint className="h-5 w-5" />
  }

  const formatDate = (date) => {
    if (!date) return 'Mai'
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WebAuthn non supportato
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (!isWebAuthnSupported) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center text-gray-500 dark:text-gray-400">
          <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
          <p className="text-sm">
            Il tuo browser non supporta le Passkeys. 
            Usa Chrome, Safari, Firefox o Edge aggiornato.
          </p>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Backup codes modal
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (backupCodes) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
            ⚠️ Codici di backup
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
            Salva questi codici in un luogo sicuro. Potrai usarli se perdi l'accesso alle passkeys.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, i) => (
              <code
                key={i}
                className="px-3 py-2 bg-white dark:bg-gray-800 rounded text-sm font-mono text-center"
              >
                {code}
              </code>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setBackupCodes(null)}
          className="w-full btn-primary"
        >
          Ho salvato i codici
        </button>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Main render
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white">
            Passkeys registrate
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {passkeys.length} di {maxPasskeys}
          </p>
        </div>
        
        {passkeys.length < maxPasskeys && !isAdding && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="btn-secondary flex items-center text-sm py-1.5 px-3"
          >
            <Plus className="h-4 w-4 mr-1" />
            Aggiungi
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Add form */}
      {isAdding && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome dispositivo
            </label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="es. iPhone personale, MacBook lavoro"
              className="input"
              maxLength={100}
              autoFocus
            />
          </div>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => {
                setIsAdding(false)
                setDeviceName('')
                clearError()
              }}
              className="btn-secondary flex-1"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleAddPasskey}
              disabled={!deviceName.trim() || loading}
              className="btn-primary flex-1 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Registrazione...
                </>
              ) : (
                <>
                  <Fingerprint className="h-4 w-4 mr-2" />
                  Registra Passkey
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Segui le istruzioni del tuo dispositivo per completare la registrazione.
          </p>
        </div>
      )}

      {/* Passkeys list */}
      {passkeys.length > 0 ? (
        <div className="space-y-2">
          {passkeys.map((passkey) => (
            <div
              key={passkey.id}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
            >
              <div className="flex items-center min-w-0">
                <div className="flex-shrink-0 text-gray-400 dark:text-gray-500 mr-3">
                  {getDeviceIcon(passkey)}
                </div>
                
                <div className="min-w-0">
                  {editingId === passkey.id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="input py-1 px-2 text-sm"
                        maxLength={100}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => handleRenamePasskey(passkey.id)}
                        disabled={loading}
                        className="p-1 text-green-600 hover:text-green-700"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="p-1 text-gray-400 hover:text-gray-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {passkey.friendlyName}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>
                          Usata: {passkey.lastUsedAt ? formatDate(passkey.lastUsedAt) : 'Mai'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {!editingId && (
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    type="button"
                    onClick={() => startEdit(passkey)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Rinomina"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  
                  {deletingId === passkey.id ? (
                    <div className="flex items-center space-x-1">
                      <button
                        type="button"
                        onClick={() => handleRemovePasskey(passkey.id)}
                        disabled={loading}
                        className="p-1.5 text-red-600 hover:text-red-700"
                        title="Conferma"
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletingId(null)}
                        className="p-1.5 text-gray-400 hover:text-gray-500"
                        title="Annulla"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeletingId(passkey.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600"
                      title="Rimuovi"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : !isAdding && (
        <div className="text-center py-6">
          <Fingerprint className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nessuna passkey registrata
          </p>
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700"
          >
            Aggiungi la tua prima passkey
          </button>
        </div>
      )}
    </div>
  )
}
